import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    BriefcaseIcon,
    EyeIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    UserIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';

interface Task {
    _id: string;
    title: string;
    description: string;
    status: string;
    budgetScenarios: Array<{
        dataCount: number;
        earnings: {
            user: number;
            teamExecutive: number;
            teamLeader: number;
            districtHead: number;
            stateHead: number;
            regionalHead: number;
            admin: number;
        };
    }>;
    assignedUsers: Array<{
        user: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
        };
        dataSubmitted: Array<{
            _id: string;
            data: any;
            submittedAt: string;
            isValidated: boolean;
        }>;
        progress: number;
    }>;
    createdAt: string;
    deadline?: string;
}

interface TaskStats {
    total: number;
    active: number;
    completed: number;
    pending: number;
    totalUsers: number;
    activeUsers: number;
}

const TrainerManagement: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [taskStats, setTaskStats] = useState<TaskStats>({
        total: 0,
        active: 0,
        completed: 0,
        pending: 0,
        totalUsers: 0,
        activeUsers: 0
    });

    useEffect(() => {
        if (user?.role === 'project_trainer') {
            fetchAssignedTasks();
        }
    }, [user]);

    const fetchAssignedTasks = async () => {
        try {
            setLoading(true);
            const response = await api.get('/tasks/user-tasks');
            const tasksData = response.data.data?.tasks || [];
            setTasks(tasksData);

            // Calculate task stats
            const stats = {
                total: tasksData.length,
                active: tasksData.filter((task: Task) => task.status === 'active').length,
                completed: tasksData.filter((task: Task) => task.status === 'completed').length,
                pending: tasksData.filter((task: Task) => task.status === 'pending').length,
                totalUsers: tasksData.reduce((sum: number, task: Task) => sum + task.assignedUsers.length, 0),
                activeUsers: tasksData.reduce((sum: number, task: Task) =>
                    sum + task.assignedUsers.filter((assignment: any) => assignment.dataSubmitted.length > 0).length, 0
                )
            };
            setTaskStats(stats);
        } catch (error) {
            console.error('Error fetching assigned tasks:', error);
            showToast({ message: 'Error fetching assigned tasks', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredTasks = tasks.filter(task => {
        const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            task.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || task.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    const getTaskProgress = (task: Task) => {
        if (task.assignedUsers.length === 0) return 0;
        const totalSubmissions = task.assignedUsers.reduce((sum, assignment) =>
            sum + assignment.dataSubmitted.length, 0
        );
        const expectedSubmissions = task.assignedUsers.length; // Assuming 1 submission per user
        return Math.round((totalSubmissions / expectedSubmissions) * 100);
    };

    const getDaysUntilDeadline = (deadline: string) => {
        const deadlineDate = new Date(deadline);
        const today = new Date();
        const diffTime = deadlineDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    if (user?.role !== 'project_trainer') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
                    <p className="text-gray-600">You don't have permission to access this page.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Project Trainer Dashboard</h1>
                    <p className="mt-2 text-gray-600">View and manage assigned tasks</p>
                </div>

                {/* Task Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <BriefcaseIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Tasks</dt>
                                    <dd className="text-lg font-medium text-gray-900">{taskStats.total}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ClockIcon className="h-8 w-8 text-yellow-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Active Tasks</dt>
                                    <dd className="text-lg font-medium text-gray-900">{taskStats.active}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                                    <dd className="text-lg font-medium text-gray-900">{taskStats.completed}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                                    <dd className="text-lg font-medium text-gray-900">{taskStats.pending}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <UserIcon className="h-8 w-8 text-purple-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                                    <dd className="text-lg font-medium text-gray-900">{taskStats.totalUsers}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-indigo-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                                    <dd className="text-lg font-medium text-gray-900">{taskStats.activeUsers}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search tasks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-5 w-5 text-gray-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                </div>

                {/* Tasks List */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">Assigned Tasks ({filteredTasks.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Users</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredTasks.map((task) => {
                                    const progress = getTaskProgress(task);
                                    const daysUntilDeadline = task.deadline ? getDaysUntilDeadline(task.deadline) : null;

                                    return (
                                        <tr key={task._id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {task.title}
                                                </div>
                                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                                    {task.description}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${task.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    task.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {task.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                                        <div
                                                            className="bg-blue-600 h-2 rounded-full"
                                                            style={{ width: `${progress}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-sm text-gray-500">{progress}%</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {task.assignedUsers.length}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {task.deadline ? (
                                                    <div className="flex items-center">
                                                        <CalendarIcon className="h-4 w-4 mr-1" />
                                                        <span className={daysUntilDeadline && daysUntilDeadline < 0 ? 'text-red-600' : ''}>
                                                            {new Date(task.deadline).toLocaleDateString()}
                                                        </span>
                                                        {daysUntilDeadline !== null && (
                                                            <span className={`ml-2 text-xs ${daysUntilDeadline < 0 ? 'text-red-600' :
                                                                daysUntilDeadline <= 3 ? 'text-yellow-600' :
                                                                    'text-gray-500'
                                                                }`}>
                                                                ({daysUntilDeadline < 0 ? 'Overdue' : `${daysUntilDeadline} days left`})
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">No deadline</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => setSelectedTask(task)}
                                                    className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                                                >
                                                    <EyeIcon className="h-4 w-4" />
                                                    <span>View Details</span>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Task Details Modal */}
                {selectedTask && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {selectedTask.title} - Task Details
                                    </h3>
                                    <button
                                        onClick={() => setSelectedTask(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <ExclamationTriangleIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    {/* Task Info */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-2">Task Information</h4>
                                        <p className="text-gray-600 mb-4">{selectedTask.description}</p>
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="font-medium">Status:</span>
                                                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${selectedTask.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    selectedTask.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {selectedTask.status}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium">Created:</span>
                                                <span className="ml-2">{new Date(selectedTask.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            {selectedTask.deadline && (
                                                <div>
                                                    <span className="font-medium">Deadline:</span>
                                                    <span className="ml-2">{new Date(selectedTask.deadline).toLocaleDateString()}</span>
                                                </div>
                                            )}
                                            <div>
                                                <span className="font-medium">Progress:</span>
                                                <span className="ml-2">{getTaskProgress(selectedTask)}%</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Assigned Users */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-4">Assigned Users ({selectedTask.assignedUsers.length})</h4>
                                        <div className="space-y-4">
                                            {selectedTask.assignedUsers.map((assignment) => (
                                                <div key={assignment.user._id} className="border rounded-lg p-4">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center space-x-3">
                                                            <UserIcon className="h-6 w-6 text-gray-400" />
                                                            <div>
                                                                <h5 className="font-medium text-gray-900">
                                                                    {assignment.user.firstName} {assignment.user.lastName}
                                                                </h5>
                                                                <p className="text-sm text-gray-500">{assignment.user.email}</p>
                                                                <p className="text-sm text-gray-500">{assignment.user.phone}</p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                Submissions: {assignment.dataSubmitted.length}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                Progress: {assignment.progress || 0}%
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {assignment.dataSubmitted.length > 0 && (
                                                        <div className="mt-3 space-y-2">
                                                            <h6 className="text-sm font-medium text-gray-700">Recent Submissions:</h6>
                                                            {assignment.dataSubmitted.slice(0, 3).map((submission, index) => (
                                                                <div key={submission._id} className="bg-white p-3 rounded border text-sm">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="font-medium">Submission #{index + 1}</span>
                                                                        <span className={`px-2 py-1 rounded-full text-xs ${submission.isValidated ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                                            }`}>
                                                                            {submission.isValidated ? 'Validated' : 'Pending'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-gray-500 mt-1">
                                                                        {new Date(submission.submittedAt).toLocaleString()}
                                                                    </p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={() => setSelectedTask(null)}
                                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrainerManagement;
